import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function Payments() {
    const { activeHotelId } = useAuth();
    const [reservations, setReservations] = useState([]);
    const [txns, setTxns] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        if (!activeHotelId) return;
        const { data } = await api.get(`/reservations?hotel_id=${activeHotelId}`);
        setReservations(data.filter(r => r.balance > 0 && ["confirmed", "checked_in"].includes(r.status)));
    };
    useEffect(() => { load(); }, [activeHotelId]);

    const stripeCheckout = async (res) => {
        setLoading(true);
        try {
            const origin = window.location.origin;
            const { data } = await api.post("/payments/stripe/create-session", {
                reservation_id: res.id,
                package_id: "deposit",
                origin_url: origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e));
            setLoading(false);
        }
    };

    const razorpayCharge = async (res) => {
        setLoading(true);
        try {
            const { data } = await api.post("/payments/razorpay/create-order", {
                reservation_id: res.id, amount: res.balance,
            });
            if (data.mocked) {
                // Simulate a client-side successful payment
                await api.post("/payments/razorpay/confirm", { order_id: data.order_id, payment_id: `pay_mock_${Date.now()}` });
                toast.success(`Razorpay MOCK charge ₹${res.balance.toLocaleString()} succeeded`);
                load();
            } else {
                // Real Razorpay checkout would open modal here — placeholder
                toast.info("Real Razorpay checkout would open. Provide RAZORPAY_KEY_ID to enable.");
            }
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };

    return (
        <AppShell title="Payments" subtitle="Stripe (LIVE test mode) · Razorpay (MOCKED)">
            <Card className="p-4 mb-4 border border-emerald-200 bg-emerald-50 rounded-md">
                <div className="text-sm text-emerald-900">
                    <strong>Stripe:</strong> real test-mode integration is LIVE — you'll be redirected to Stripe's hosted checkout. Use card <code className="font-mono">4242 4242 4242 4242</code> with any future expiry, any CVC, any ZIP.
                </div>
            </Card>
            <Card className="p-4 mb-4 border border-amber-200 bg-amber-50 rounded-md">
                <div className="text-sm text-amber-900">
                    <strong>Razorpay:</strong> MOCKED. Set RAZORPAY_KEY_ID + RAZORPAY_KEY_SECRET in <code className="font-mono">/app/backend/.env</code> to enable.
                </div>
            </Card>

            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-3">Outstanding Balances</div>
            <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                        <tr><th className="px-4 py-3">Guest</th><th className="px-4 py-3">Reservation</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Balance</th><th className="px-4 py-3 text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                        {reservations.map(r => (
                            <tr key={r.id} className="border-b border-slate-100" data-testid={`pay-row-${r.id}`}>
                                <td className="px-4 py-3 text-sm font-medium">{r.guest_name}</td>
                                <td className="px-4 py-3 text-xs text-slate-500">{r.check_in_date?.slice(0, 10)} → {r.check_out_date?.slice(0, 10)}</td>
                                <td className="px-4 py-3 text-sm font-mono">₹{Number(r.total).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm font-mono font-semibold text-red-600">₹{Number(r.balance).toLocaleString()}</td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex gap-1 justify-end">
                                        <Button size="sm" disabled={loading} onClick={() => stripeCheckout(r)} className="h-7 text-xs bg-[#635BFF] hover:bg-[#5148E5] text-white" data-testid={`stripe-${r.id}`}>
                                            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CreditCard className="w-3 h-3 mr-1" /> Stripe ₹5,000</>}
                                        </Button>
                                        <Button size="sm" variant="outline" disabled={loading} onClick={() => razorpayCharge(r)} className="h-7 text-xs" data-testid={`rzp-${r.id}`}>
                                            Razorpay
                                        </Button>
                                        <a href={`${API_BASE}/invoices/reservation/${r.id}`} target="_blank" rel="noreferrer">
                                            <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`pdf-${r.id}`}>
                                                <ExternalLink className="w-3 h-3 mr-1" /> PDF
                                            </Button>
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {reservations.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-sm text-slate-500">No outstanding balances</td></tr>}
                    </tbody>
                </table>
            </Card>
        </AppShell>
    );
}
